import React from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader,
  Typography, 
  Box, 
  Stack,
  IconButton
} from '@mui/material';
import { 
  Edit, 
  Euro, 
  People as Users, 
  Delete as Trash2 
} from '@mui/icons-material';
import { ApartmentType } from '@/types';
import EditApartmentTypeForm from '@/components/apartmentType/EditApartmentTypeForm';
import { useI18n } from '@/hooks/useI18n';

interface ApartmentTypeCardProps {
  apartmentType: ApartmentType;
  onUpdateApartmentType: (scheme: ApartmentType) => void;
  onDeleteApartmentType: (schemeId: string) => void;
}

const ApartmentTypeCard: React.FC<ApartmentTypeCardProps> = ({ 
  apartmentType, 
  onUpdateApartmentType, 
  onDeleteApartmentType 
}) => {
  const { t } = useI18n();

  return (
    <Card sx={{ 
      '&:hover': { 
        boxShadow: 3 
      },
      transition: 'box-shadow 0.2s ease'
    }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            🏷️ {apartmentType.name}
          </Box>
        }
        action={
          <Stack direction="row" spacing={1} alignItems="center" sx={{ pt:1 }}>
            <EditApartmentTypeForm
              apartmentType={apartmentType}
              onUpdateApartmentType={onUpdateApartmentType}
              trigger={
                <IconButton size="small">
                  <Edit sx={{ fontSize: 16 }} />
                </IconButton>
              }
            />
            <IconButton 
              size="small" 
              color="error"
              onClick={() => onDeleteApartmentType(apartmentType.id)}
            >
              <Trash2 sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        }
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ pt: 0 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'medium' }}>
            {t('apartmentType.form.priceRules')}
          </Typography>
          <Stack spacing={1}>
            {apartmentType.priceRules
              .sort((a, b) => a.occupancy - b.occupancy)
              .map((rule, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    p: 1.5,
                    bgcolor: 'grey.50',
                    borderRadius: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Users sx={{ fontSize: 16 }} />
                    <Typography variant="body2" color="text.secondary">
                      {t('apartmentType.form.occupancy')}: {rule.occupancy}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Euro sx={{ fontSize: 16 }} />
                    <Typography variant="body2" fontWeight="medium">
                      {rule.pricePerPersonPerWeek}
                    </Typography>
                  </Box>
                </Box>
              ))}
          </Stack>
          {apartmentType.priceRules.length === 0 && (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
              {t('apartmentType.list.noRules')}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ApartmentTypeCard;
